export function safeRatio (numerator, denominator) {
  const denominatorValue = Number(denominator || 0)
  if (!denominatorValue) {
    return 0
  }

  return Number(numerator || 0) / denominatorValue
}

export function toPercent (ratio) {
  return Math.round(safeRatio(ratio, 1) * 100)
}

export function getTopCompetitor (rows) {
  return rows.filter(row => !row.isTrackedDomain).reduce((topCompetitor, row) => {
    if (!topCompetitor) {
      return row
    }

    return Number(row.mentionCount || 0) > Number(topCompetitor.mentionCount || 0)
      ? row
      : topCompetitor
  }, null)
}

export function normalizeCompetitorRows (rows, metrics) {
  const totalMentionCount = metrics?.totalMentionCount || 0

  return rows.map((row, index) => {
    const totalAnswerCount = metrics?.totalAnswerCount || 0
    const displayName = getDisplayName(row.domain)
    const shareRatio = safeRatio(row.mentionCount, totalMentionCount)
    const mentionRateRatio = safeRatio(row.mentionedAnswerCount, totalAnswerCount)

    return {
      ...row,
      rank: index + 1,
      domain: row.domain,
      displayName,
      initial: getInitial(displayName),
      shareRatio,
      sharePercent: toPercent(shareRatio),
      mentionRateRatio,
      mentionRatePercent: toPercent(mentionRateRatio),
      averagePositionLabel: formatAveragePosition(row.averagePosition),
      mentionedAnswersLabel: `in ${Number(row.mentionedAnswerCount || 0)} of ${Number(totalAnswerCount || 0)} answers`,
      mentionCountLabel: `${Number(row.mentionCount || 0)} mentions`,
      headToHead: getHeadToHeadDisplay(row),
      isTrackedDomain: Boolean(row.isTrackedDomain),
      isWatched: Boolean(row.isWatched),
    }
  })
}

export function buildCompetitorsSummary (metrics, normalizedRows) {
  const topCompetitor = getTopCompetitor(normalizedRows)
  const trackedRow = normalizedRows.find(row => row.isTrackedDomain)
  const trends = metrics?.trends || {}
  const totalAnswerCount = Number(metrics?.totalAnswerCount || 0)

  return {
    yourRankLabel: trackedRow ? `#${trackedRow.rank}` : '-',
    yourMentionRatePercent: Number(metrics?.yourMentionRate || 0),
    yourMentionedAnswerCount: Number(metrics?.yourMentionedAnswerCount || 0),
    totalAnswerCount,
    topCompetitor,
    topCompetitorName: topCompetitor?.brandName || topCompetitor?.domain || '-',
    topCompetitorMentionRatePercent: Number(metrics?.topCompetitorMentionRate || 0),
    topCompetitorPositionLabel: topCompetitor ? `#${topCompetitor.rank}` : '-',
    totalCompetitors: Number(metrics?.totalCompetitors || normalizedRows.length || 0),
    competitorsAheadCount: Number(metrics?.competitorsAheadCount || 0),
    yourMentionRateTrendDisplay: getTrendDisplay(trends.yourMentionRate, '%', 'No change'),
    topCompetitorMentionRateTrendDisplay: getTrendDisplay(trends.topCompetitorMentionRate, '%', 'No change'),
    totalCompetitorsTrendDisplay: getCompetitorCountTrendDisplay(trends.totalCompetitors),
  }
}

export function getDisplayName (domain) {
  return domain;
  // const normalizedDomain = (domain || '').replace(/^www\./i, '')
  // const firstLabel = normalizedDomain.split('.')[0]
  //
  // return firstLabel || normalizedDomain || '-'
}

function getInitial (displayName) {
  const match = (displayName || '').match(/[a-z0-9]/i)
  return match ? match[0].toUpperCase() : '-'
}

function formatAveragePosition (averagePosition) {
  if (averagePosition === null || averagePosition === undefined) {
    return '-'
  }

  return `${Number(averagePosition).toFixed(0)}`
}

export function getHeadToHeadDisplay (row) {
  const youBeatCount = Number(row.youBeatCount || 0)
  const theyBeatCount = Number(row.theyBeatCount || 0)
  const finalResult = youBeatCount - theyBeatCount

  if (row.isTrackedDomain) {
    return {
      label: 'Your site',
      tone: 'info',
    }
  }
  if (finalResult < 0) {
    return {
      label: `They lead by ${-finalResult}`,
      tone: 'danger',
    }
  }
  if (finalResult > 0) {
    return {
      label: `You lead by ${finalResult}`,
      tone: 'success',
    }
  }

  return {
    label: 'Even',
    tone: 'neutral',
  }
}

function getTrendDisplay (trend, unit = '', flatLabel = '-', periodLabel = ' this week') {
  if (trend === null) {
    return null
  }

  const trendValue = Number(trend || 0)

  if (trendValue > 0) {
    return {
      direction: 'up',
      tone: 'positive',
      label: `${formatSignedTrendValue(trendValue, unit)}${periodLabel}`,
    }
  }
  if (trendValue < 0) {
    return {
      direction: 'down',
      tone: 'negative',
      label: `${formatSignedTrendValue(trendValue, unit)}${periodLabel}`,
    }
  }

  return {
    direction: 'flat',
    tone: 'neutral',
    label: flatLabel,
  }
}

function formatSignedTrendValue (value, unit) {
  const spacer = unit && unit !== '%' ? ' ' : ''
  return `${value > 0 ? '+' : ''}${value}${unit ? `${spacer}${unit}` : ''}`
}

function getCompetitorCountTrendDisplay (trend) {
  if (trend === null) {
    return null
  }

  const trendValue = Number(trend || 0)

  if (trendValue > 0) {
    return {
      direction: 'up',
      tone: 'positive',
      label: `+${trendValue} new competitors`,
    }
  }
  if (trendValue < 0) {
    return {
      direction: 'down',
      tone: 'negative',
      label: `${Math.abs(trendValue)} fewer competitors`,
    }
  }

  return {
    direction: 'flat',
    tone: 'neutral',
    label: 'No change',
  }
}

export const INITIAL_BACKGROUND_COLORS = [
  '#FF5700',
  '#06B6D4',
  '#92B1F5',
  '#8B5CF6',
  '#EF4444',
  '#10B981',
  '#F59E0B',
]
